"use client";

import * as React from "react";
import Link from "next-shims/Link";
import Image from "next-shims/Image";
import {
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
  Box,
} from "@mui/material";

import BedOutlinedIcon from "@mui/icons-material/BedOutlined";
import BathtubOutlinedIcon from "@mui/icons-material/BathtubOutlined";
import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

export type PropertyCardItem = {
  id: string;
  slug: string;
  display_title: string;
  city?: string;
  region?: string;
  listing_status?: "rent" | "sale" | string;
  label?: string | null;
  persons?: string | null;
  price?: number | null;
  price_currency?: string;
  price_on_call?: boolean;
  bedrooms?: number | null;
  bathrooms?: number | null;
  size_m2?: number | null;
  coverImage?: string | null;
};

function formatPrice(p: PropertyCardItem) {
  if (p.price_on_call) return "Price on call";
  if (p.price == null) return "";
  const cur = p.price_currency ?? "EUR";
  return `${p.price.toLocaleString("el-GR")} ${cur}`;
}

export default function PropertyCard({
  locale,
  property,
}: {
  locale: string;
  property: PropertyCardItem;
}) {
  const href = `/${locale}/properties/${property.slug}`;
  const isSale = String(property.listing_status || "").includes("sale");
  const badge = isSale ? "Πώληση" : "Ενοικίαση";
  const showPersons = !!property.persons && !isSale; // ✅ persons μόνο σε rent

  return (
    <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
      <CardActionArea component={Link} href={href}>
        <Box sx={{ position: "relative", height: 210, bgcolor: "grey.100" }}>
          {property.coverImage ? (
            <Image
              src={property.coverImage}
              alt={property.display_title}
              fill
              sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 33vw"
              style={{ objectFit: "cover" }}
            />
          ) : null}

          {/* ✅ Premium readable badges */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ position: "absolute", top: 12, left: 12 }}
          >
            <Chip
              size="small"
              label={badge}
              sx={{
                color: "#fff",
                fontWeight: 900,
                bgcolor: "rgba(0,0,0,0.58)",
                backdropFilter: "blur(6px)",
                border: "1px solid rgba(255,255,255,0.18)",
                textShadow: "0 1px 2px rgba(0,0,0,0.35)",
              }}
            />

            {property.label ? (
              <Chip
                size="small"
                label={property.label}
                sx={{
                  color: "#111",
                  fontWeight: 900,
                  bgcolor: "rgba(255,255,255,0.86)",
                  backdropFilter: "blur(6px)",
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              />
            ) : null}
          </Stack>
        </Box>

        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
            {property.display_title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {[property.city, property.region].filter(Boolean).join(", ")}
          </Typography>

          {/* ✅ Premium icon spec row */}
          <Stack
            direction="row"
            spacing={1.2}
            sx={{ mt: 1 }}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            {property.bedrooms != null && (
              <Stack direction="row" spacing={0.6} alignItems="center">
                <BedOutlinedIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {property.bedrooms}
                </Typography>
              </Stack>
            )}

            {property.bathrooms != null && (
              <Stack direction="row" spacing={0.6} alignItems="center">
                <BathtubOutlinedIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {property.bathrooms}
                </Typography>
              </Stack>
            )}

            {property.size_m2 != null && (
              <Stack direction="row" spacing={0.6} alignItems="center">
                <SquareFootOutlinedIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {property.size_m2} m²
                </Typography>
              </Stack>
            )}

            {showPersons && (
              <Stack direction="row" spacing={0.6} alignItems="center">
                <GroupsOutlinedIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {property.persons}
                </Typography>
              </Stack>
            )}
          </Stack>

          <Typography variant="body1" sx={{ mt: 1.2, fontWeight: 900 }}>
            {formatPrice(property)}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
